import { mergeDeep } from "remeda"
import { ProviderCalendarId } from "@/entities/providerCalendar"
import { type InvoiceUploadSubmitValues } from "@/features/invoiceUpload"
import { getAnnualConsumption } from "@/features/invoiceUpload/utils/getAnnualConsumption"
import { getIsInvoiceOld } from "@/features/invoiceUpload/utils/getIsInvoiceOld"
import { hasElectricity, hasGas } from "@/features/subscription"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { onboardingDataLayer, OnboardingDataLayerEvent } from "../../datalayer"
import { useInitialOnboardingFormContext } from "../context/InitialOnboardingFormContext"
import { useEligibilityCheckQuote } from "./useEligibilityCheckQuote"

const PROVIDER_CALENDAR_MAPPING = {
    ALL_DAY: ProviderCalendarId.BASE,
    PEAK_OFF_PEAK: ProviderCalendarId.HPHC,
}

function isProviderCalendarIdMapped(
    tariffOption: string | null
): tariffOption is "ALL_DAY" | "PEAK_OFF_PEAK" {
    return tariffOption === "ALL_DAY" || tariffOption === "PEAK_OFF_PEAK"
}

const CIVILITY_TO_TITLE: Record<string, string> = {
    Monsieur: "M.",
    Madame: "Ms.",
}

export function useValidateInvoiceUpload() {
    const { selectedEnergyTypes, journeyType, onboardingState } = useInitialOnboardingFormContext()

    const { validateMeterPoints, error } = useEligibilityCheckQuote({
        stepName: OnboardingRouteNames.MeterDetails,
    })

    const submit = async (submitValues: InvoiceUploadSubmitValues) => {
        const { invoiceMeterPointId, givenName, familyName, invoiceResult } = submitValues

        const newOnboardingState = {
            formValues: structuredClone(onboardingState.formValues),
            stepHistory: [OnboardingRouteNames.MeterDetails],
            submittedSteps: [],
        }

        const meterPoints = []

        if (hasElectricity(selectedEnergyTypes)) {
            meterPoints.push({
                givenName,
                familyName,
                meterPointId: invoiceMeterPointId,
                marketName: "FRA_ELECTRICITY" as const,
                journeyType,
            })

            newOnboardingState.formValues.electricity.givenName = givenName
            newOnboardingState.formValues.electricity.familyName = familyName

            if (invoiceResult) {
                newOnboardingState.formValues = mergeDeep(newOnboardingState.formValues, {
                    electricity: {
                        providerCalendarId: isProviderCalendarIdMapped(invoiceResult.tariffOption)
                            ? PROVIDER_CALENDAR_MAPPING[invoiceResult.tariffOption]
                            : invoiceResult.tariffOption,
                        annualConsumptionReference: getAnnualConsumption(
                            invoiceResult.annualConsumption,
                            invoiceResult.tariffOption
                        ),
                        invoiceUpload: {
                            amountDue:
                                invoiceResult.amountDueMonthly != null
                                    ? invoiceResult.amountDueMonthly * 12
                                    : null,
                            isInvoiceOld: getIsInvoiceOld(invoiceResult.invoiceIssueDate),
                            extractionUuid: invoiceResult.extractionUuid,
                        },
                        supplyPoint: {
                            meterPoint: {
                                providerCalendarId: invoiceResult.tariffOption,
                            },
                        },
                    },
                    email: invoiceResult.client?.email || "",
                    title: invoiceResult.client?.civility
                        ? CIVILITY_TO_TITLE[invoiceResult.client.civility]
                        : "",
                })
            }
        }

        //TODO: handle gas invoice data when supported by the API, example below
        // WARN : this feature is available only for electricity, below is only an example
        if (hasGas(selectedEnergyTypes)) {
            meterPoints.push({
                givenName,
                familyName,
                meterPointId: invoiceMeterPointId,
                marketName: "FRA_GAS" as const,
                journeyType,
            })

            newOnboardingState.formValues.gas.givenName = givenName
            newOnboardingState.formValues.gas.familyName = familyName

            if (invoiceResult) {
                newOnboardingState.formValues = mergeDeep(newOnboardingState.formValues, {
                    gas: {
                        annualConsumptionReference:
                            invoiceResult.annualConsumption?.allDay != null
                                ? { allDay: invoiceResult.annualConsumption.allDay }
                                : null,
                        invoiceUpload: {
                            amountDue:
                                invoiceResult.amountDueMonthly != null
                                    ? invoiceResult.amountDueMonthly * 12
                                    : null,
                            isInvoiceOld: getIsInvoiceOld(invoiceResult.invoiceIssueDate),
                            extractionUuid: invoiceResult.extractionUuid,
                        },
                    },
                })
            }
        }

        onboardingDataLayer.sendTrackingEvent(
            OnboardingDataLayerEvent.InvoiceUploadFormSent,
            newOnboardingState.formValues
        )

        await validateMeterPoints(meterPoints, newOnboardingState)
    }

    return { submit, error }
}
